import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ShoppingCart, ArrowLeft, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { useCart } from '../contexts/CartContext';

const ProductDetailPage = () => {
  const { sku } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const data = await api.getProduct(sku);
        setProduct(data);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [sku]);

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
  };

  const incrementQuantity = () => setQuantity(q => q + 1);
  const decrementQuantity = () => setQuantity(q => Math.max(1, q - 1));

  const getDefaultImage = () => {
    if (!product?.imageUrl) {
      return '/images/placeholder-product.jpg';
    }
    return product.imageUrl;
  };

  if (loading) {
    return (
      <div className="paper-texture min-h-screen flex items-center justify-center">
        <div className="inline-block w-12 h-12 border-4 border-cape-red border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="paper-texture min-h-screen flex flex-col items-center justify-center">
        <h1 className="font-display text-4xl mb-4">Product Not Found</h1>
        <Link to="/shop">
          <Button className="btn-primary">
            Return to Shop
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="paper-texture min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Back Button */}
        <Link to="/shop" className="inline-flex items-center space-x-2 text-[#2D2A26] hover:text-cape-red mb-8 transition-colors" data-testid="back-to-shop">
          <ArrowLeft className="w-5 h-5" />
          <span className="font-semibold uppercase tracking-wider text-sm">Back to Shop</span>
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <div className="relative">
            <div className="bg-alt-bg aspect-square overflow-hidden soft-shadow">
              <img
                src={getDefaultImage()}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
              />
            </div>
            {product.featured && (
              <span className="absolute top-6 left-6 bg-gold text-deep-berry px-4 py-2 font-bold uppercase tracking-wider">
                Featured
              </span>
            )}
            <span className="absolute top-6 right-6 category-badge text-base px-4 py-2">
              {product.category}
            </span>
          </div>

          {/* Product Details */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <h1 className="font-display text-4xl lg:text-5xl mb-4" data-testid="product-name">
              {product.name}
            </h1>
            
            <div className="price-tag text-3xl mb-6" data-testid="product-price">
              ${product.retailPrice.toFixed(2)}
            </div>

            <div className="border-t border-b border-alt-bg py-6 mb-6">
              <p className="text-lg text-[#2D2A26] leading-relaxed" data-testid="product-description">
                {product.description}
              </p>
            </div>

            {/* Quantity Selector */}
            <div className="mb-8">
              <label className="block text-sm font-semibold uppercase tracking-wider mb-3">
                Quantity
              </label>
              <div className="flex items-center space-x-4">
                <button
                  onClick={decrementQuantity}
                  className="w-12 h-12 border-2 border-alt-bg hover:border-cape-red flex items-center justify-center transition-colors"
                  data-testid="decrement-quantity"
                >
                  <Minus className="w-5 h-5" />
                </button>
                <span className="text-2xl font-bold w-12 text-center" data-testid="quantity-display">
                  {quantity}
                </span>
                <button
                  onClick={incrementQuantity}
                  className="w-12 h-12 border-2 border-alt-bg hover:border-cape-red flex items-center justify-center transition-colors"
                  data-testid="increment-quantity"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <Button
              onClick={handleAddToCart}
              className="btn-primary w-full flex items-center justify-center space-x-3 text-base py-6"
              data-testid="add-to-cart-button"
            >
              <ShoppingCart className="w-5 h-5" />
              <span>Add to Cart</span>
            </Button>

            {/* Product Info */}
            <div className="mt-8 space-y-4 text-sm">
              <div className="flex justify-between py-3 border-b border-alt-bg">
                <span className="font-semibold uppercase tracking-wider">SKU</span>
                <span className="text-[#5C5855]">{product.sku}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-alt-bg">
                <span className="font-semibold uppercase tracking-wider">Category</span>
                <span className="text-[#5C5855]">{product.category}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-alt-bg">
                <span className="font-semibold uppercase tracking-wider">Availability</span>
                <span className="text-sage font-semibold">In Stock</span>
              </div>
            </div>

            {/* Additional Info */}
            <div className="mt-8 p-6 bg-alt-bg">
              <h3 className="font-display text-xl mb-3">Made on Cape Cod</h3>
              <p className="text-sm text-[#5C5855] leading-relaxed">
                All our preserves are crafted in small batches in Osterville, Massachusetts, using traditional methods that have been passed down through generations.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
